import type { QueryClient } from "@tanstack/react-query";
import type { createStore } from "jotai";

import { refreshAfterCapabilityUpdate } from "@/components/Assistant/refreshAfterCapabilityUpdate";
import { DEPLOYMENTS_QUERY } from "@/components/AppManagementV2/deploymentsQuery";
import { pendingFocusNodeIdAtom } from "@/store/editor-jotai";
import { agentFormRefreshAtom } from "@/store/assistant-editor-jotai";
import { genieWroteConversationAtom } from "@/store/conversational";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { isRecord } from "@/utils/isRecord";

export interface ToolEffectContext {
  queryClient: QueryClient;
  store: ReturnType<typeof createStore>;
  // The call's args, for a tool whose output does not name what it touched.
  input: unknown;
}

export type ToolHandler = (output: unknown, ctx: ToolEffectContext) => void;

// The Deploy tab keeps its own query, outside DATA_KEYS.
const refreshDeployments: ToolHandler = (_output, { queryClient }) => {
  queryClient.invalidateQueries({ queryKey: [DEPLOYMENTS_QUERY] });
};

// Only that agent's query; a failed refetch rejects, so the editor is not
// reset onto what it already had. A copy cached without a workspace (asked
// for before the workspace loaded) fails on every refetch and is not the one
// the editor reads, so it is left out.
const refetchAgent = (queryClient: QueryClient, agentId: string) =>
  queryClient.refetchQueries(
    {
      queryKey: [DATA_KEYS.ASSISTANT_ID, { path: { assistant_id: agentId } }],
      predicate: ({ queryKey }) => {
        const params = queryKey[1];
        return (
          isRecord(params) &&
          isRecord(params.path) &&
          typeof params.path.group_id === "string" &&
          params.path.group_id !== ""
        );
      },
    },
    { throwOnError: true }
  );

function agentIdArg(input: unknown): string | undefined {
  const id =
    typeof input === "object" && input !== null && "agent_id" in input
      ? (input as { agent_id?: unknown }).agent_id
      : undefined;
  return typeof id === "string" ? id : undefined;
}

const showConversationChange: ToolHandler = (_output, { store, input }) => {
  if (isRecord(input) && typeof input.conversation_id === "string")
    store.set(genieWroteConversationAtom, {
      conversationId: input.conversation_id,
    });
};

// Each delete tool and the argument naming what it deleted.
const DELETED_BY: Partial<Record<string, string>> = {
  noxus_workflows_delete: "workflow_id",
  noxus_agents_delete: "agent_id",
  noxus_tables_delete: "table_id",
  noxus_knowledge_bases_delete: "kb_id",
};

// Where an open chat's page goes once the chat is gone.
function leaveChat(url: URL): string {
  const agent = url.searchParams.get("assistant_id");
  return agent ? `/assistant/${agent}?tab=past-runs` : "/";
}

// A delete whose thing has a page of its own, not an open tool: where the
// page shows it, and where it goes once it is gone.
const LEFT_BY: Partial<
  Record<
    string,
    (input: Record<string, unknown>, url: URL) => string | undefined
  >
> = {
  noxus_conversations_delete: ({ conversation_id }, url) => {
    if (url.pathname !== `/chat/${conversation_id}`) return undefined;
    return leaveChat(url);
  },
  noxus_deployments_delete: ({ deployment_id }, url) => {
    if (url.searchParams.get("deployment_id") !== deployment_id)
      return undefined;
    url.searchParams.delete("deployment_id");
    return `${url.pathname}${url.search}`;
  },
};

export function pageLeftBy(
  toolName: string,
  input: unknown,
  output: unknown,
  url: URL
): string | undefined {
  const leave = LEFT_BY[toolName];
  if (!leave || !isRecord(input) || !isRecord(output)) return undefined;
  if (output.success === false) return undefined;
  return leave(input, new URL(url));
}

// A delete by filter names no chat it deleted: the open chat, when the
// filter could have matched it, is checked before the page leaves it.
export function chatMaybeDeletedBy(
  toolName: string,
  input: unknown,
  url: URL
): { conversationId: string; leaveTo: string } | undefined {
  if (toolName !== "noxus_conversations_delete_matching" || !isRecord(input))
    return undefined;
  const conversationId = url.pathname.match(/^\/chat\/([^/]+)$/)?.[1];
  if (!conversationId) return undefined;
  const agent = url.searchParams.get("assistant_id");
  if (typeof input.agent_id === "string" && agent && input.agent_id !== agent)
    return undefined;
  return { conversationId, leaveTo: leaveChat(url) };
}

// A delete that reports it failed (a knowledge base's ``success: false``)
// removed nothing.
export function deletedToolId(
  toolName: string,
  input: unknown,
  output: unknown
): string | undefined {
  const arg = DELETED_BY[toolName];
  if (!arg || !isRecord(input)) return undefined;
  if (!isRecord(output) || output.success === false) return undefined;
  const id = input[arg];
  return typeof id === "string" ? id : undefined;
}

export const TOOL_HANDLERS: Partial<Record<string, ToolHandler>> = {
  noxus_v1_workflows_add_node: (output, { store }) => {
    const out = output as { node_id?: string; nodeId?: string } | undefined;
    const nodeId = out?.node_id ?? out?.nodeId;
    if (typeof nodeId === "string") {
      store.set(pendingFocusNodeIdAtom, nodeId);
    }
  },

  noxus_agents_update: (_output, { queryClient, store, input }) => {
    const agentId = agentIdArg(input);
    if (!agentId) return;
    void refetchAgent(queryClient, agentId).then(
      () => store.set(agentFormRefreshAtom, { agentId, remount: false }),
      () => undefined
    );
  },

  // The same refresh as the capability dialogs: a pin can change the form.
  noxus_agents_pin_capabilities: (_output, { queryClient, store, input }) => {
    const agentId = agentIdArg(input);
    if (!agentId) return;
    void refreshAfterCapabilityUpdate(queryClient, agentId, () =>
      store.set(agentFormRefreshAtom, { agentId, remount: true })
    );
  },

  // The whole definition is replaced, so the form is rebuilt, not merged.
  noxus_agents_restore_version: (_output, { queryClient, store, input }) => {
    const agentId = agentIdArg(input);
    if (!agentId) return;
    void refetchAgent(queryClient, agentId).then(
      () => store.set(agentFormRefreshAtom, { agentId, remount: true }),
      () => undefined
    );
  },

  noxus_conversations_chat: showConversationChange,
  noxus_conversations_regenerate: showConversationChange,
  noxus_conversations_rate_message: showConversationChange,
  noxus_conversations_stop: showConversationChange,

  noxus_agents_import: refreshDeployments,
  noxus_deployments_create: refreshDeployments,
  noxus_deployments_activate: refreshDeployments,
  noxus_deployments_deactivate: refreshDeployments,
  noxus_deployments_delete: refreshDeployments,
};
